import { BREAKPOINT_QUERY } from '@/hooks/breakpoint-queries';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useInboxPermissionAccess } from '@/hooks/use-inbox-permission-access';
import { useInboxQueryScope } from '@/hooks/use-inbox-query-scope';
import { useDropQueryWhenUnobserved } from '@/hooks/use-drop-query-when-unobserved';
import { useAssistantFollowUps } from '@/hooks/use-assistant-follow-ups';
import { useState, useCallback, useEffect, useMemo, useRef, type KeyboardEvent } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, MessageCircle, PanelRightOpen, X } from 'lucide-react';
import { ConversationListSidebar } from '@/components/inbox/conversation-list-sidebar';
import { NewConversationPanel } from '@/components/inbox/new-conversation-panel';
import {
  ConversationDetailsPanel,
  type DetailsMetaThread,
  type DetailsPanelClient,
} from '@/components/inbox/conversation-details-panel';
import { ExistingConversationPanel } from '@/components/inbox/existing-conversation-panel';
import { InboxBackButton, InboxDetailsButton } from '@/components/inbox/inbox-thread-header-buttons';
import { ClientDetailDrawer } from '@/features/clients';
import { useGetLocations } from '@/api/generated/locations/locations';
import { useGetConversationsId, getGetConversationsIdQueryKey } from '@/api/generated/conversations/conversations';
import { inboxClientQueryKey } from '@/hooks/inbox-conversation-cache';
import { useInboxConversationLists, useInboxThreadPrefetch } from '@/hooks/use-inbox-conversation-lists';
import { useGetClientsId } from '@/api/generated/clients/clients';
import type { ClientListItem } from '@/api/generated/schemas';
import { cn } from '@/lib/utils';
import { PageCanvas } from '@/components/layout/page-canvas';
import { useCanWriteOperational } from '@/providers';
import { useClientPermissionAccess } from '@/hooks/use-client-permission-access';
import { formatDocumentTitle } from '@/lib/document-title';

type StatusFilter = 'OPEN' | 'CLOSED';

type InboxSearch = { conversation?: string };

export const Route = createFileRoute('/_auth/$orgId/inbox')({
  // `?conversation=<id>` opens that thread (deep links from Marketing › Reviews); consumed on arrival.
  validateSearch: (search: Record<string, unknown>): InboxSearch => ({
    conversation: typeof search.conversation === 'string' && search.conversation ? search.conversation : undefined,
  }),
  component: InboxPage,
});

function InboxPage() {
  const access = useInboxPermissionAccess();
  const { scopeKey } = useClientPermissionAccess();
  if (!access.canViewAll) return <PageCanvas><p className="p-6">You do not have access to Inbox.</p></PageCanvas>;
  return <InboxContent key={scopeKey} />;
}

function InboxContent() {
  const { orgId } = Route.useParams();
  const { conversation: linkedConversationId } = Route.useSearch();
  const navigate = useNavigate({ from: '/$orgId/inbox' });
  const clientAccess = useClientPermissionAccess();
  const inboxAccess = useInboxPermissionAccess();
  const canWriteOperational = useCanWriteOperational() && inboxAccess.canSend;
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(linkedConversationId ?? null);
  const [isNewConversation, setIsNewConversation] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientListItem | null>(null);
  const [drawerClient, setDrawerClient] = useState<ClientListItem | null>(null);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('OPEN');
  // "Save as client" on a thread's shared-phone banner: the details form opens with that phone.
  const [sharedPhoneDraft, setSharedPhoneDraft] = useState<{ conversationId: string; phone: string } | null>(null);
  const detailsCloseButtonRef = useRef<HTMLButtonElement | null>(null);
  const detailsDialogRef = useRef<HTMLDivElement | null>(null);
  const detailsTriggerRef = useRef<HTMLElement | null>(null);
  const isXlViewport = useMediaQuery(BREAKPOINT_QUERY.xlUp);

  const { data: locationsData, isLoading: isLoadingLocations } = useGetLocations();
  const orgTimezone = locationsData?.data?.[0]?.timezone || undefined;

  // Conversation lists for auto-select after close/reopen (the sidebar reads the same queries)
  const { open: { data: openData }, closed: { data: closedData } } = useInboxConversationLists(statusFilter);
  const detailKey = useInboxQueryScope(getGetConversationsIdQueryKey(selectedConversationId ?? ''));
  const prefetchThread = useInboxThreadPrefetch(orgId);
  // The selected thread's list row: its header and client are known before the thread detail lands.
  const listRow = useMemo(
    () => [...(openData?.data?.items ?? []), ...(closedData?.data?.items ?? [])]
      .find((row) => row.id === selectedConversationId) ?? null,
    [openData, closedData, selectedConversationId]
  );

  const handleStatusFilterChange = useCallback((filter: StatusFilter) => {
    setStatusFilter(filter);
    setSelectedConversationId(null);
    setSelectedClient(null);
    setIsDetailsPanelOpen(false);
  }, []);

  // The sidebar's "Needs you" group sits above the list (same cached query).
  const { data: followUpData } = useAssistantFollowUps();
  const handleConversationStatusChanged = useCallback(() => {
    const currentList = statusFilter === 'OPEN' ? openData : closedData;
    // Next thread in on-screen order: the "Needs you" group first, then the list without those threads.
    const followUpIds = (followUpData?.data?.items ?? []).map((item) => item.conversationId);
    const listIds = (currentList?.data?.items ?? []).map((c) => c.id).filter((id) => !followUpIds.includes(id));
    const remaining = [...followUpIds, ...listIds].filter((id) => id !== selectedConversationId);

    setSelectedConversationId(remaining[0] ?? null);
    setSelectedClient(null);
    setIsDetailsPanelOpen(false);
  }, [statusFilter, openData, closedData, followUpData, selectedConversationId]);

  // Fetch conversation details for selected existing conversation
  // Same gate as the thread panel: without individual access the API refuses, so don't fetch (and
  // don't hold the details skeleton through its retries).
  const conversationQueryEnabled =
    !!selectedConversationId && !isNewConversation && inboxAccess.canViewIndividual;
  const { data: conversationData, isPending: isConversationPending } = useGetConversationsId(selectedConversationId ?? '', {
    query: { enabled: conversationQueryEnabled, queryKey: detailKey },
  });
  // The list row names the client already, so its details load alongside the thread instead of after it.
  const conversationClientId = conversationData ? conversationData.data?.client?.id : listRow?.client?.id;
  const conversationClientQueryKey = useMemo(
    () => inboxClientQueryKey(orgId, conversationClientId, clientAccess.scopeKey),
    [orgId, conversationClientId, clientAccess.scopeKey]
  );

  useDropQueryWhenUnobserved(conversationClientQueryKey);

  useEffect(() => {
    setSelectedClient(null);
    setDrawerClient(null);
  }, [clientAccess.scopeKey]);

  // Fetch full client data for details panel
  const { data: conversationClientData, isLoading: isLoadingClient } = useGetClientsId(
    conversationClientId ?? '',
    undefined,
    {
      query: {
        queryKey: conversationClientQueryKey,
        enabled: !!conversationClientId && clientAccess.ready,
        retry: false,
      },
    }
  );

  const conversationDetail = conversationData?.data;
  const metaThread = useMemo<DetailsMetaThread | null>(() => {
    const contacts = conversationDetail?.metaContacts ?? [];
    if (!conversationDetail || contacts.length === 0) return null;
    return { conversationId: conversationDetail.id, contacts, linked: !!conversationDetail.client };
  }, [conversationDetail]);

  // Save / link / unlink moved the thread: follow it to the conversation the server returned.
  const handleConversationChanged = useCallback((conversationId: string) => {
    setSelectedConversationId(conversationId);
    setSharedPhoneDraft(null);
  }, []);

  const handleSaveSharedPhone = (phone: string) => {
    if (!selectedConversationId) return;
    setSharedPhoneDraft({ conversationId: selectedConversationId, phone });
    if (!isXlViewport) handleOpenDetailsPanel();
  };

  const existingConversationClient = conversationClientData?.data
    ? (conversationClientData.data as unknown as DetailsPanelClient)
    : null;

  const handleNewConversation = () => {
    if (!canWriteOperational) return;
    setIsNewConversation(true);
    setSelectedConversationId(null);
    setSelectedClient(null);
    setIsDetailsPanelOpen(false);
  };

  const handleCloseNewConversation = () => {
    setIsNewConversation(false);
    setSelectedClient(null);
    setIsDetailsPanelOpen(false);
  };

  // A deep link selects its thread (also when Inbox is already open), then leaves the URL clean so a
  // refresh or later selection isn't pulled back to it.
  useEffect(() => {
    if (!linkedConversationId) return;
    setSelectedConversationId(linkedConversationId);
    setIsNewConversation(false);
    setSelectedClient(null);
    setIsDetailsPanelOpen(false);
    void navigate({ search: {}, replace: true });
  }, [linkedConversationId, navigate]);

  const handleSelectConversation = (id: string) => {
    setSelectedConversationId(id);
    setIsNewConversation(false);
    setSelectedClient(null);
    setIsDetailsPanelOpen(false);
  };

  const handleClientSelected = (client: ClientListItem) => {
    setSelectedClient(client);
  };

  const handleClientCleared = () => {
    setSelectedClient(null);
  };

  const handleClientCreatedFromDetails = (client: ClientListItem) => {
    setSelectedClient(client);
  };

  const handleConversationCreated = (conversationId: string) => {
    setIsNewConversation(false);
    setSelectedConversationId(conversationId);
    setSelectedClient(null);
    setIsDetailsPanelOpen(false);
  };

  const hasConversationContext = isNewConversation || !!selectedConversationId;
  // An open thread carries Back and Details in its own header row below lg; at lg–xl the labelled strip stays.
  const threadHasOwnHeader = !isNewConversation && !!selectedConversationId;

  useEffect(() => {
    if (canWriteOperational || !isNewConversation) return;
    setIsNewConversation(false);
    setSelectedClient(null);
    setIsDetailsPanelOpen(false);
  }, [canWriteOperational, isNewConversation]);

  useEffect(() => {
    if (!isDetailsPanelOpen || isXlViewport) return;

    detailsCloseButtonRef.current?.focus();
  }, [isDetailsPanelOpen, isXlViewport]);

  useEffect(() => {
    if (isXlViewport && isDetailsPanelOpen) {
      setIsDetailsPanelOpen(false);
    }
  }, [isDetailsPanelOpen, isXlViewport]);

  const handleOpenDetailsPanel = () => {
    detailsTriggerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setIsDetailsPanelOpen(true);
  };

  const handleCloseDetailsPanel = () => {
    setIsDetailsPanelOpen(false);
    detailsTriggerRef.current?.focus();
  };

  const handleDetailsDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleCloseDetailsPanel();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = detailsDialogRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable?.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleBackToConversations = () => {
    if (isNewConversation) {
      handleCloseNewConversation();
      return;
    }
    setSelectedConversationId(null);
    setSelectedClient(null);
    setIsDetailsPanelOpen(false);
  };

  const renderDetailsPanel = (hideHeader = false) => (
    <ConversationDetailsPanel
      selectedClient={isNewConversation ? selectedClient : existingConversationClient}
      onClientClick={(client) => setDrawerClient(client)}
      onClientCreated={handleClientCreatedFromDetails}
      timezone={orgTimezone}
      timezoneLoading={isLoadingLocations}
      orgId={orgId}
      // Skeleton until everything the panel needs is known — otherwise it flashes an empty state:
      // the conversation (names the client), client permissions + the client itself. The org timezone only
      // holds the appointment rows (they shimmer until it is known).
      isLoading={
        !isNewConversation &&
        ((conversationQueryEnabled && isConversationPending) ||
          (!!conversationClientId && !clientAccess.ready) ||
          isLoadingClient)
      }
      hideHeader={hideHeader}
      metaThread={isNewConversation ? null : metaThread}
      sharedPhone={sharedPhoneDraft?.conversationId === selectedConversationId ? sharedPhoneDraft?.phone : null}
      onConversationChanged={handleConversationChanged}
    />
  );

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Inbox')}</title>
      </Helmet>
      <PageCanvas className="relative h-full min-h-0 overflow-hidden">
        <div
          className={cn(
            'grid h-full min-h-0 overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface shadow-soft-lg',
            hasConversationContext
              ? 'grid-cols-1 md:grid-cols-[minmax(300px,340px)_minmax(0,1fr)] xl:grid-cols-[minmax(300px,340px)_minmax(0,1fr)_minmax(320px,360px)]'
              : 'grid-cols-1 md:grid-cols-[minmax(300px,340px)_minmax(0,1fr)]'
          )}
        >
          {/* Left sidebar - conversation list */}
          <div
            className={cn(
              'min-h-0 min-w-0 overflow-hidden',
              hasConversationContext ? 'hidden md:block' : 'block'
            )}
          >
            <ConversationListSidebar
              selectedId={selectedConversationId}
              onSelect={handleSelectConversation}
              isNewConversation={isNewConversation}
              onNewConversation={handleNewConversation}
              onCloseNewConversation={handleCloseNewConversation}
              selectedClient={selectedClient}
              statusFilter={statusFilter}
              onStatusFilterChange={handleStatusFilterChange}
              readOnly={!canWriteOperational}
              onConversationIntent={prefetchThread}
            />
          </div>

          {/* Center panel - conversation content */}
          <div
            className={cn(
              'min-h-0 min-w-0 flex-col overflow-hidden bg-clovie-surface',
              hasConversationContext ? 'flex' : 'hidden md:flex'
            )}
          >
            {hasConversationContext && (
              <div
                className={cn(
                  'flex min-h-12 items-center justify-between gap-2 border-b border-clovie-border-soft bg-clovie-surface px-3 xl:hidden',
                  threadHasOwnHeader && 'max-lg:hidden'
                )}
              >
                <button
                  type="button"
                  onClick={handleBackToConversations}
                  className="inline-flex min-h-11 items-center gap-2 rounded-[var(--r-pill)] px-2.5 text-[16px] font-semibold text-clovie-muted transition-colors hover:bg-clovie-hover hover:text-clovie-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clovie-brand md:hidden"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Inbox
                </button>
                <span className="hidden md:block" />
                <button
                  type="button"
                  onClick={handleOpenDetailsPanel}
                  className="inline-flex min-h-11 items-center gap-2 rounded-[var(--r-pill)] px-2.5 text-[16px] font-semibold text-clovie-muted transition-colors hover:bg-clovie-hover hover:text-clovie-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clovie-brand"
                  aria-label="Open conversation details"
                >
                  <PanelRightOpen className="h-4 w-4" />
                  Details
                </button>
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-hidden">
              {isNewConversation && canWriteOperational ? (
                <NewConversationPanel
                  key={clientAccess.scopeKey}
                  onClientSelected={handleClientSelected}
                  onClientCleared={handleClientCleared}
                  selectedClient={selectedClient}
                  onConversationCreated={handleConversationCreated}
                />
              ) : !selectedConversationId ? (
                <InboxEmptyState />
              ) : (
                <ExistingConversationPanel
                  conversationId={selectedConversationId}
                  timezone={orgTimezone}
                  onStatusChanged={handleConversationStatusChanged}
                  onSwitchToOpen={() => setStatusFilter('OPEN')}
                  readOnly={false}
                  onConversationChanged={handleConversationChanged}
                  onSaveSharedPhone={handleSaveSharedPhone}
                  headerLeading={<InboxBackButton onClick={handleBackToConversations} />}
                  headerTrailing={<InboxDetailsButton onClick={handleOpenDetailsPanel} />}
                  listRow={listRow}
                />
              )}
            </div>
          </div>

          {/* Right panel - details */}
          {hasConversationContext && isXlViewport && (
            <div className="hidden min-h-0 min-w-0 overflow-hidden border-clovie-border-soft xl:block xl:border-l">
              {renderDetailsPanel()}
            </div>
          )}
        </div>

        {hasConversationContext && !isXlViewport && isDetailsPanelOpen && (
          <div className="fixed inset-0 z-50 xl:hidden">
            <div
              className="absolute inset-0 cursor-default bg-black/20"
              aria-hidden="true"
              onClick={handleCloseDetailsPanel}
            />
            <div
              ref={detailsDialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="inbox-details-panel-title"
              onKeyDown={handleDetailsDialogKeyDown}
              className="absolute inset-y-0 right-0 flex w-full max-w-[380px] flex-col overflow-hidden border-l border-clovie-border-soft bg-clovie-surface shadow-soft-lg sm:w-[min(380px,calc(100vw-32px))]"
            >
              <div className="flex min-h-12 items-center justify-between border-b border-clovie-border-soft bg-clovie-surface px-4">
                <h2
                  id="inbox-details-panel-title"
                  className="text-[16px] font-semibold text-clovie-text"
                >
                  Conversation details
                </h2>
                <button
                  ref={detailsCloseButtonRef}
                  type="button"
                  onClick={handleCloseDetailsPanel}
                  className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full text-clovie-muted transition-colors hover:bg-clovie-hover hover:text-clovie-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clovie-brand"
                  aria-label="Close conversation details"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-hidden">{renderDetailsPanel(true)}</div>
            </div>
          </div>
        )}
      </PageCanvas>

      {/* Client Detail Drawer */}
      <ClientDetailDrawer
        open={drawerClient !== null}
        onClose={() => setDrawerClient(null)}
        client={
          drawerClient
            ? {
                id: drawerClient.id,
                firstName: drawerClient.firstName,
                lastName: drawerClient.lastName,
                email: drawerClient.email,
                phone: drawerClient.phone,
                avatarUrl: drawerClient.avatarUrl,
                isActive: drawerClient.isActive,
                createdAt: drawerClient.createdAt,
                notes: drawerClient.notes ?? undefined,
              }
            : null
        }
      />
    </>
  );
}

function InboxEmptyState() {
  return (
    <div className="flex h-full items-center justify-center bg-clovie-surface-2 px-6 text-center">
      <div className="max-w-xs">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-clovie-chip text-clovie-chip-fg shadow-soft">
          <MessageCircle className="h-5 w-5" />
        </span>
        <p className="mt-4 text-[16px] font-semibold text-clovie-text">Select a conversation</p>
        <p className="mt-1 text-[16px] leading-6 text-clovie-muted">
          Choose a thread from the inbox to read messages and reply.
        </p>
      </div>
    </div>
  );
}
